import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { forgotUser } from "./ForgotForm.api";
import { useMutation } from "@tanstack/react-query";
import { toastrError, toastrSuccess } from "./../../lib/toastr";
import LoadingOverlay from "react-loading-overlay-ts";
import { ForgotValidation } from "./ForgotValidation";
import forgot from '../../assets/Forgot/forgot.avif';
import { FiKey, FiLock, FiMail, FiShield } from "react-icons/fi";
import { AuthCard, AuthLayout, BrandFeature, BrandPanel } from "components/LoginForm/AuthLayout";

const RECOVERY_STEPS: BrandFeature[] = [
  { icon: FiMail, title: "Check your inbox", text: "Open the reset email sent to your registered address." },
  { icon: FiLock, title: "Choose a new password", text: "Follow the link and set a password of at least 6 characters." },
  { icon: FiShield, title: "Sign in securely", text: "Log back in with your new password to continue earning." },
];
const intialState = {
  emailId: "",
  app: "",
  errors: {
    email: "",
  },
};

export const  ForgotForm = () => {
  const { t } = useTranslation();
  const [formValue, setFormValue] = useState(intialState);
  const { emailId } = formValue;
  const mutation = useMutation(() => forgotUser(emailId), {
    onSuccess: (response: any) => {
      if (response.success) {
        toastrSuccess(response.message);
        setFormValue(intialState);
      } else {
        toastrError(response.message);
      }
    },
    onError: () => {
      toastrError(t("page.unsubscribe.Encountered"));
    },
  });

  const setErrors = (errorData: any) => {
    setFormValue((old) => ({ ...old, errors: { ...errorData } }));
  };
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const valid = ForgotValidation(formValue, setErrors, t);
    if (valid) {
      mutation.mutate();
    }
  };
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValue({ ...formValue, [name]: value });
  };

  return (
    <LoadingOverlay
      styles={{
        overlay: (base) => ({
          ...base,
          position: "fixed",
        }),
      }}
      active={mutation.isLoading}
      spinner
      text={t("claim.card.Loading")}
    >
      <AuthLayout
        aside={
          <BrandPanel
            eyebrow="Account recovery"
            title="Get back into your account"
            description="Enter the email you registered with and we'll send you a link to reset your password."
            features={RECOVERY_STEPS}
            image={forgot}
          />
        }
      >
        <AuthCard
          icon={FiKey}
          title={t("form.forgot.ForgotPassword")}
          subtitle="We'll email you a secure link to choose a new password."
          footer={
            <p>
              Remembered it?{" "}
              <a href="/login" className="zs-link">
                Back to log in
              </a>
            </p>
          }
        >
          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            <div data-testid="email" className="flex flex-col">
              <label htmlFor="email" aria-label="email" className="zs-label zs-label-required">
                {t("form.forgot.Enteryouremail")}
              </label>
              <input
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                title="Please enter email properly"
                type="text"
                name="emailId"
                id="email"
                value={emailId}
                onChange={onInputChange}
                placeholder="ZampliaSurveys@gmail.com "
                aria-invalid={formValue.errors.email ? true : undefined}
                className="zs-input h-11"
              />
              {formValue.errors.email ? (
                <p className="zs-error" id="emai_err_msg">
                  {formValue.errors.email}
                </p>
              ) : null}
            </div>
            <div data-testid="button">
              <button type="submit" className="zs-btn zs-btn-primary zs-btn-lg zs-btn-block">
                {t("forgot.btn.Submit")}
              </button>
            </div>
          </form>
        </AuthCard>
      </AuthLayout>
    </LoadingOverlay>
  );
};
